<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import axios from 'axios'
import HeaderPanel from '@/components/HeaderPanel.vue'
import { useUserStore } from '@/stores/user'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'

defineProps({
  hideHeader: { type: Boolean, default: false }
})

const userStore = useUserStore()
const toast = useToast()
const confirm = useConfirm()

// Используем тот же базовый URL, что и остальные страницы (UTM, auth и т.п.)
const apiBase = 'https://ncht.bz'

const loading = ref(false)
const entries = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(100)
const sortField = ref('created_at')
const sortOrder = ref(-1)
const PAGE_SIZE_STORAGE_KEY = 'wisdomBase.pageSize'

const filters = ref({
  search: '',
  season: null,
  category: '',
  subcategory: '',
  speaker: '',
  fileType: null,
  fileFormat: null,
  topic: '',
  subtopic: '',
  favoriteOnly: false,
  showHidden: false
})

const fileTypes = [
  { value: 'recording', label: 'Запись' },
  { value: 'book', label: 'Книга' },
  { value: 'outline', label: 'Конспект' },
  { value: 'template', label: 'Шаблон' },
  { value: 'presentation', label: 'Презентация' },
  { value: 'workbook', label: 'Рабочая тетрадь' },
  { value: 'checklist', label: 'Чек-лист' },
  { value: 'miro_map', label: 'Miro-карта' },
  { value: 'methodology', label: 'Методология' },
  { value: 'example', label: 'Пример' },
  { value: 'screencast', label: 'Скринкаст' },
  { value: 'collection', label: 'Подборка' },
  { value: 'guide', label: 'Гайд' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'ai_agent', label: 'AI-агент' },
  { value: 'psychometric_test', label: 'Тест' },
  { value: 'framework', label: 'Фреймворк' },
  { value: 'photo', label: 'Фото' },
  { value: 'music', label: 'Музыка' },
  { value: 'tilda', label: 'Tilda' }
]

const fileFormats = [
  { value: 'video', label: 'Видео' },
  { value: 'link', label: 'Ссылка' },
  { value: 'pdf', label: 'PDF' },
  { value: 'gdrive', label: 'Google Диск' },
  { value: 'spreadsheet', label: 'Таблица' },
  { value: 'notion', label: 'Notion' },
  { value: 'document', label: 'Документ' },
  { value: 'figma', label: 'Figma' },
  { value: 'form', label: 'Форма' }
]

const fileTypeLabel = value => fileTypes.find(t => t.value === value)?.label || value
const fileFormatLabel = value => fileFormats.find(t => t.value === value)?.label || value

const filterOptions = ref({
  seasons: [],
  speakers: [],
  categories: [],
  subcategories: []
})

const seasons = computed(() => filterOptions.value.seasons)
const speakers = computed(() => filterOptions.value.speakers)
const categories = computed(() => filterOptions.value.categories)
const subcategories = computed(() => filterOptions.value.subcategories)

let searchDebounce = null

async function loadEntries() {
  if (!userStore.token) {
    const ok = await userStore.checkAuth()
    if (!ok) {
      return
    }
  }

  loading.value = true
  try {
    const params = {
      page: page.value,
      pageSize: pageSize.value,
      sortField: sortField.value,
      sortOrder: sortOrder.value === 1 ? 'asc' : 'desc'
    }

    if (filters.value.search) params.search = filters.value.search
    if (filters.value.season != null) params.season = filters.value.season
    if (filters.value.category) params.category = filters.value.category
    if (filters.value.subcategory) params.subcategory = filters.value.subcategory
    if (filters.value.speaker) params.speaker = filters.value.speaker
    if (filters.value.fileType) params.fileType = filters.value.fileType
    if (filters.value.fileFormat) params.fileFormat = filters.value.fileFormat
    if (filters.value.topic) params.topic = filters.value.topic
    if (filters.value.subtopic) params.subtopic = filters.value.subtopic
    if (filters.value.favoriteOnly) params.favoriteOnly = 'true'
    if (filters.value.showHidden) params.includeHidden = 'true'

    const { data } = await axios.get(`${apiBase}/api/wisdom/entries`, {
      params,
      headers: {
        Authorization: `Bearer ${userStore.token}`
      }
    })

    entries.value = (data.items || []).map(item => ({
      ...item,
      category: item.category ?? '',
      tags: item.tags ?? '',
      is_hidden: item.is_hidden === true,
      has_cover: item.has_cover === true
    }))
    total.value = data.total || 0
    prefetchCovers(entries.value)
  } catch (err) {
    console.error('[Wisdom] loadEntries error', err)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить данные Базы мудрости',
      life: 4000
    })
  } finally {
    loading.value = false
  }
}

async function loadFilterOptions() {
  if (!userStore.token) {
    const ok = await userStore.checkAuth()
    if (!ok) return
  }

  try {
    const { data } = await axios.get(`${apiBase}/api/wisdom/entries/filter-options`, {
      params: {
        includeHidden: 'true'
      },
      headers: {
        Authorization: `Bearer ${userStore.token}`
      }
    })

    filterOptions.value = {
      seasons: data?.seasons || [],
      speakers: data?.speakers || [],
      categories: data?.categories || data?.blocks || [],
      subcategories: data?.subcategories || []
    }
  } catch (err) {
    console.error('[Wisdom] loadFilterOptions error', err)
  }
}

function onPage(event) {
  page.value = event.page + 1
  pageSize.value = event.rows
  localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(event.rows))
  loadEntries()
}

function onSort(event) {
  sortField.value = event.sortField || 'created_at'
  sortOrder.value = event.sortOrder || -1
  loadEntries()
}

function onSearchInput() {
  if (searchDebounce) {
    clearTimeout(searchDebounce)
  }
  searchDebounce = setTimeout(() => {
    page.value = 1
    loadEntries()
  }, 400)
}

function resetFilters() {
  filters.value = {
    search: '',
    season: null,
    category: '',
    subcategory: '',
    speaker: '',
    fileType: null,
    fileFormat: null,
    topic: '',
    subtopic: '',
    favoriteOnly: false,
    showHidden: false
  }
  page.value = 1
  loadEntries()
}

const editDialogVisible = ref(false)
const editMode = ref('create')
const currentEntry = ref(null)
const coverUrls = ref({})
const coverFile = ref(null)
const coverRemoved = ref(false)
const coverPreviewUrl = ref('')

function authHeaders() {
  return { Authorization: `Bearer ${userStore.token}` }
}

function revokeCoverUrl(id) {
  const url = coverUrls.value[id]
  if (url) {
    URL.revokeObjectURL(url)
    const next = { ...coverUrls.value }
    delete next[id]
    coverUrls.value = next
  }
}

async function ensureCoverUrl(id) {
  if (!id || coverUrls.value[id]) return
  try {
    const { data } = await axios.get(`${apiBase}/api/wisdom/entries/${id}/cover`, {
      headers: authHeaders(),
      responseType: 'blob'
    })
    if (coverUrls.value[id]) URL.revokeObjectURL(coverUrls.value[id])
    coverUrls.value = {
      ...coverUrls.value,
      [id]: URL.createObjectURL(data)
    }
  } catch {
    // нет обложки
  }
}

function prefetchCovers(list) {
  for (const item of list || []) {
    if (item?.has_cover && item.id) ensureCoverUrl(item.id)
  }
}

function resetCoverEditor() {
  coverFile.value = null
  coverRemoved.value = false
  if (coverPreviewUrl.value && !Object.values(coverUrls.value).includes(coverPreviewUrl.value)) {
    URL.revokeObjectURL(coverPreviewUrl.value)
  }
  coverPreviewUrl.value = ''
}

function onCoverFileChange(event) {
  const file = event.target.files && event.target.files[0]
  event.target.value = ''
  if (!file) return
  const type = String(file.type || '').toLowerCase()
  if (type !== 'image/png' && type !== 'image/jpeg' && type !== 'image/jpg') {
    toast.add({
      severity: 'warn',
      summary: 'Обложка',
      detail: 'Нужен PNG или JPG',
      life: 4000
    })
    return
  }
  if (file.size > 5 * 1024 * 1024) {
    toast.add({
      severity: 'warn',
      summary: 'Обложка',
      detail: 'Файл больше 5 МБ',
      life: 4000
    })
    return
  }
  if (coverPreviewUrl.value && !Object.values(coverUrls.value).includes(coverPreviewUrl.value)) {
    URL.revokeObjectURL(coverPreviewUrl.value)
  }
  coverFile.value = file
  coverRemoved.value = false
  coverPreviewUrl.value = URL.createObjectURL(file)
}

function clearCover() {
  if (coverPreviewUrl.value && !Object.values(coverUrls.value).includes(coverPreviewUrl.value)) {
    URL.revokeObjectURL(coverPreviewUrl.value)
  }
  coverFile.value = null
  coverRemoved.value = true
  coverPreviewUrl.value = ''
}

async function syncCover(entryId) {
  if (!entryId) return
  if (coverRemoved.value && !coverFile.value) {
    await axios.delete(`${apiBase}/api/wisdom/entries/${entryId}/cover`, {
      headers: authHeaders()
    })
    revokeCoverUrl(entryId)
    return
  }
  if (coverFile.value) {
    const form = new FormData()
    form.append('file', coverFile.value)
    await axios.put(`${apiBase}/api/wisdom/entries/${entryId}/cover`, form, {
      headers: authHeaders()
    })
    revokeCoverUrl(entryId)
  }
}

function normalizeTranscriptions(value) {
  if (!Array.isArray(value)) return []
  return value.map((item, index) => ({
    id: item?.id ?? null,
    title: item?.title || `Контент ${index + 1}`,
    content: item?.content || ''
  }))
}

function normalizeMaterialAirs(value) {
  if (!Array.isArray(value)) return []
  return value.map((item, index) => ({
    title: item?.title || `Эфир ${index + 1}`,
    url: item?.url || ''
  }))
}

function addTranscription() {
  if (!currentEntry.value) return
  if (!Array.isArray(currentEntry.value.transcriptions)) {
    currentEntry.value.transcriptions = []
  }
  currentEntry.value.transcriptions.push({
    id: null,
    title: `Контент ${currentEntry.value.transcriptions.length + 1}`,
    content: ''
  })
}

function removeTranscription(index) {
  if (!currentEntry.value || !Array.isArray(currentEntry.value.transcriptions)) return
  currentEntry.value.transcriptions.splice(index, 1)
}

function addMaterialAir() {
  if (!currentEntry.value) return
  if (!Array.isArray(currentEntry.value.material_airs)) {
    currentEntry.value.material_airs = []
  }
  currentEntry.value.material_airs.push({
    title: `Эфир ${currentEntry.value.material_airs.length + 1}`,
    url: ''
  })
}

function removeMaterialAir(index) {
  if (!currentEntry.value || !Array.isArray(currentEntry.value.material_airs)) return
  currentEntry.value.material_airs.splice(index, 1)
}

function openCreateDialog() {
  editMode.value = 'create'
  resetCoverEditor()
  currentEntry.value = {
    title: '',
    season: null,
    category: '',
    subcategory: '',
    speaker: '',
    description: '',
    material_url: '',
    file_type: null,
    file_format: null,
    topic: '',
    subtopic: '',
    tags: '',
    is_favorite: false,
    is_hidden: false,
    has_cover: false,
    transcriptions: [],
    material_airs: []
  }
  editDialogVisible.value = true
}

function openEditDialog(row) {
  editMode.value = 'edit'
  resetCoverEditor()
  currentEntry.value = {
    ...row,
    category: row.category ?? '',
    tags: row.tags ?? '',
    is_hidden: row.is_hidden === true,
    has_cover: row.has_cover === true,
    transcriptions: normalizeTranscriptions(row.content || row.transcriptions),
    material_airs: normalizeMaterialAirs(row.material_airs)
  }
  coverPreviewUrl.value = coverUrls.value[row.id] || ''
  if (row.has_cover && row.id) ensureCoverUrl(row.id).then(() => {
    if (!coverFile.value && !coverRemoved.value) {
      coverPreviewUrl.value = coverUrls.value[row.id] || coverPreviewUrl.value
    }
  })
  editDialogVisible.value = true
}

async function saveEntry() {
  if (!currentEntry.value) return

  const payload = { ...currentEntry.value }
  payload.content = normalizeTranscriptions(payload.transcriptions)
    .map(item => ({ title: item.title, content: item.content }))
  delete payload.transcriptions
  payload.material_airs = normalizeMaterialAirs(payload.material_airs)
    .map(item => ({ title: item.title, url: item.url }))
  delete payload.cover
  delete payload.cover_mime
  delete payload.has_cover

  if (!payload.title) {
    toast.add({
      severity: 'warn',
      summary: 'Проверка',
      detail: 'Название обязательно',
      life: 4000
    })
    return
  }

  try {
    const config = {
      headers: authHeaders()
    }

    let savedId = payload.id
    if (editMode.value === 'create') {
      const { data } = await axios.post(`${apiBase}/api/wisdom/entries`, payload, config)
      savedId = data?.id
      await syncCover(savedId)
      toast.add({
        severity: 'success',
        summary: 'Сохранено',
        detail: 'Запись добавлена',
        life: 3000
      })
    } else {
      const id = payload.id
      const updatePayload = { ...payload }
      delete updatePayload.id
      await axios.put(`${apiBase}/api/wisdom/entries/${id}`, updatePayload, config)
      await syncCover(id)
      toast.add({
        severity: 'success',
        summary: 'Сохранено',
        detail: 'Запись обновлена',
        life: 3000
      })
    }

    editDialogVisible.value = false
    resetCoverEditor()
    await loadEntries()
  } catch (err) {
    console.error('[Wisdom] saveEntry error', err)
    const message = err?.response?.data?.message || 'Не удалось сохранить запись'
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: message,
      life: 5000
    })
  }
}

function confirmDelete(entry) {
  confirm.require({
    message: 'Удалить эту запись?',
    header: 'Подтверждение удаления',
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: 'Удалить',
    rejectLabel: 'Отмена',
    acceptClass: 'p-button-danger',
    accept: () => deleteEntry(entry)
  })
}

async function deleteEntry(entry) {
  try {
    await axios.delete(`${apiBase}/api/wisdom/entries/${entry.id}`, {
      headers: {
        Authorization: `Bearer ${userStore.token}`
      }
    })
    if (currentEntry.value?.id === entry.id) {
      editDialogVisible.value = false
    }
    toast.add({
      severity: 'success',
      summary: 'Удалено',
      detail: 'Запись удалена',
      life: 3000
    })
    await loadEntries()
  } catch (err) {
    console.error('[Wisdom] deleteEntry error', err)
    const message = err?.response?.data?.message || 'Не удалось удалить запись'
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: message,
      life: 5000
    })
  }
}

async function toggleFavorite(entry) {
  try {
    const next = !entry.is_favorite
    const { data } = await axios.patch(
      `${apiBase}/api/wisdom/entries/${entry.id}/favorite`,
      { is_favorite: next },
      {
        headers: {
          Authorization: `Bearer ${userStore.token}`
        }
      }
    )
    entry.is_favorite = data.is_favorite
  } catch (err) {
    console.error('[Wisdom] toggleFavorite error', err)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить избранное',
      life: 4000
    })
  }
}

async function toggleHidden(entry) {
  try {
    const next = !entry.is_hidden
    const { data } = await axios.put(
      `${apiBase}/api/wisdom/entries/${entry.id}`,
      { is_hidden: next },
      {
        headers: {
          Authorization: `Bearer ${userStore.token}`
        }
      }
    )
    entry.is_hidden = data?.is_hidden === true
  } catch (err) {
    console.error('[Wisdom] toggleHidden error', err)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить статус скрытия',
      life: 4000
    })
  }
}

function splitKeywords(value) {
  if (!value) return []
  return value
    .split(/[;,]/)
    .map(s => s.trim())
    .filter(Boolean)
}

onMounted(() => {
  const storedPageSize = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY))
  if (Number.isFinite(storedPageSize) && storedPageSize > 0) {
    pageSize.value = storedPageSize
  }
  loadFilterOptions()
  loadEntries()
})

onUnmounted(() => {
  Object.values(coverUrls.value).forEach(url => URL.revokeObjectURL(url))
  if (coverPreviewUrl.value && !Object.values(coverUrls.value).includes(coverPreviewUrl.value)) {
    URL.revokeObjectURL(coverPreviewUrl.value)
  }
})

watch(
  () => [
    filters.value.season,
    filters.value.category,
    filters.value.subcategory,
    filters.value.speaker,
    filters.value.fileType,
    filters.value.fileFormat,
    filters.value.topic,
    filters.value.subtopic,
    filters.value.favoriteOnly,
    filters.value.showHidden
  ],
  () => {
    page.value = 1
    loadEntries()
  }
)
</script>

<template>
  <HeaderPanel v-if="!hideHeader" />
  <ToastVue />
  <ConfirmDialog />

  <div class="wisdom-page">
    <header class="wisdom-header">
      <div>
        <h1>База мудрости</h1>
        <p class="subtitle">
          Централизованное хранилище методичек, презентаций, записей и других материалов.
        </p>
      </div>
      <slot name="header-actions" />
    </header>

    <section class="wisdom-filters">
      <div class="filters-main">
        <div class="field grow">
          <label>Поиск</label>
          <InputText
            v-model="filters.search"
            placeholder="Название, тема, подтема или ключевые слова"
            @input="onSearchInput"
          />
        </div>
        <div class="field small">
          <label>Сезон</label>
          <DropdownVue
            v-model="filters.season"
            :options="seasons"
            :showClear="true"
            :placeholder="'Все'"
          />
        </div>
        <div class="field small">
          <label>Тип файла</label>
          <DropdownVue
            v-model="filters.fileType"
            :options="fileTypes"
            optionLabel="label"
            optionValue="value"
            showClear
            placeholder="Все"
          />
        </div>
        <div class="field small">
          <label>Формат</label>
          <DropdownVue
            v-model="filters.fileFormat"
            :options="fileFormats"
            optionLabel="label"
            optionValue="value"
            showClear
            placeholder="Все"
          />
        </div>
      </div>

      <div class="filters-secondary">
        <div class="field">
          <label>Категория</label>
          <DropdownVue
            v-model="filters.category"
            :options="categories"
            showClear
            placeholder="Все"
          />
        </div>
        <div class="field">
          <label>Подкатегория</label>
          <DropdownVue
            v-model="filters.subcategory"
            :options="subcategories"
            showClear
            placeholder="Все"
          />
        </div>
        <div class="field">
          <label>Спикер</label>
          <DropdownVue
            v-model="filters.speaker"
            :options="speakers"
            showClear
            placeholder="Все"
          />
        </div>
        <div class="field">
          <label>Тема</label>
          <InputText v-model="filters.topic" placeholder="Тема" />
        </div>
        <div class="field">
          <label>Подтема</label>
          <InputText v-model="filters.subtopic" placeholder="Подтема" />
        </div>
        <div class="field favorite-toggle">
          <ToggleSwitch v-model="filters.favoriteOnly" />
          <span>Только избранное</span>
        </div>
        <div class="field favorite-toggle">
          <ToggleSwitch v-model="filters.showHidden" />
          <span>Показывать скрытые</span>
        </div>
        <div class="field actions">
          <ButtonVue
            type="button"
            label="Сбросить фильтры"
            severity="secondary"
            text
            @click="resetFilters"
          />
        </div>
      </div>
    </section>

    <section class="wisdom-table-card">
      <div class="table-header">
        <h2>Материалы</h2>
        <div class="table-actions">
          <ButtonVue
            type="button"
            label="Добавить запись"
            icon="pi pi-plus"
            @click="openCreateDialog"
          />
        </div>
      </div>

      <DataTable
        :value="entries"
        :lazy="true"
        :loading="loading"
        :totalRecords="total"
        :paginator="true"
        :rows="pageSize"
        :rowsPerPageOptions="[100, 500, 1000]"
        :first="(page - 1) * pageSize"
        dataKey="id"
        :sortField="sortField"
        :sortOrder="sortOrder"
        @page="onPage"
        @sort="onSort"
        size="small"
        class="wisdom-table"
      >
        <ColumnTable
          field="is_favorite"
          header=""
          :style="{ width: '8.5rem' }"
          :sortable="true"
        >
          <template #body="{ data }">
            <div class="row-actions">
              <ButtonVue
                type="button"
                :icon="data.is_favorite ? 'pi pi-star-fill' : 'pi pi-star'"
                :severity="data.is_favorite ? 'warning' : 'secondary'"
                text
                rounded
                @click="toggleFavorite(data)"
              />
              <ButtonVue
                type="button"
                :icon="data.is_hidden ? 'pi pi-eye' : 'pi pi-eye-slash'"
                text
                rounded
                :severity="data.is_hidden ? 'success' : 'secondary'"
                @click="toggleHidden(data)"
              />
              <ButtonVue
                type="button"
                icon="pi pi-pencil"
                text
                rounded
                @click="openEditDialog(data)"
              />
            </div>
          </template>
        </ColumnTable>

        <ColumnTable field="title" header="Название" sortable>
          <template #body="{ data }">
            <div class="title-cell">
              <a
                v-if="data.material_url"
                :href="data.material_url"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ data.title }}
              </a>
              <span v-else>
                {{ data.title }}
              </span>
              <small v-if="data.topic" class="topic-sub">
                {{ data.topic }}<span v-if="data.subtopic"> • {{ data.subtopic }}</span>
              </small>
            </div>
          </template>
        </ColumnTable>

        <ColumnTable header="Обложка" :style="{ width: '6rem' }">
          <template #body="{ data }">
            <img
              v-if="coverUrls[data.id]"
              :src="coverUrls[data.id]"
              class="cover-thumb"
              alt=""
            />
            <span v-else class="cover-empty">—</span>
          </template>
        </ColumnTable>

        <ColumnTable field="season" header="Сезон" sortable :style="{ width: '6rem' }" />
        <ColumnTable field="category" header="Категория" sortable />
        <ColumnTable field="subcategory" header="Подкатегория" sortable />
        <ColumnTable field="speaker" header="Спикер" sortable />

        <ColumnTable field="file_type" header="Тип" sortable :style="{ width: '9rem' }">
          <template #body="{ data }">
            {{ fileTypeLabel(data.file_type) }}
          </template>
        </ColumnTable>

        <ColumnTable field="file_format" header="Формат" sortable :style="{ width: '9rem' }">
          <template #body="{ data }">
            {{ fileFormatLabel(data.file_format) }}
          </template>
        </ColumnTable>

        <ColumnTable field="tags" header="Теги" :style="{ width: '10rem', maxWidth: '10rem' }">
          <template #body="{ data }">
            <div class="keywords">
              <span v-for="kw in splitKeywords(data.tags)" :key="kw" class="keyword-chip">
                {{ kw }}
              </span>
            </div>
          </template>
        </ColumnTable>

        <template #empty>
          <div class="empty-state">
            <p>Записей пока нет.</p>
            <ButtonVue
              type="button"
              label="Добавить первую запись"
              icon="pi pi-plus"
              @click="openCreateDialog"
            />
          </div>
        </template>
      </DataTable>
    </section>

    <DialogVue
      v-model:visible="editDialogVisible"
      modal
      :style="{ width: 'min(820px, 96vw)' }"
      :header="editMode === 'create' ? 'Новая запись' : 'Редактирование записи'"
    >
      <div v-if="currentEntry" class="edit-form">
        <div class="form-grid">
          <div class="field full">
            <label>Название *</label>
            <InputText v-model="currentEntry.title" />
          </div>
          <div class="field small">
            <label>Сезон</label>
            <InputText
              v-model="currentEntry.season"
            />
          </div>
          <div class="field small">
            <label>Категория</label>
            <InputText v-model="currentEntry.category" />
          </div>
          <div class="field small">
            <label>Подкатегория</label>
            <InputText v-model="currentEntry.subcategory" />
          </div>
          <div class="field small">
            <label>Спикер</label>
            <InputText v-model="currentEntry.speaker" />
          </div>
          <div class="field full">
            <label>Ссылка на материал</label>
            <InputText v-model="currentEntry.material_url" placeholder="https://..." />
          </div>
          <div class="field full">
            <label>Обложка</label>
            <div class="cover-editor">
              <img
                v-if="coverPreviewUrl"
                :src="coverPreviewUrl"
                class="cover-preview"
                alt=""
              />
              <span v-else class="cover-empty">Нет обложки</span>
              <div class="cover-editor-actions">
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  @change="onCoverFileChange"
                />
                <ButtonVue
                  v-if="coverPreviewUrl || (currentEntry.has_cover && !coverRemoved)"
                  type="button"
                  label="Убрать"
                  severity="secondary"
                  text
                  size="small"
                  @click="clearCover"
                />
              </div>
            </div>
          </div>
          <div class="field small">
            <label>Тип файла</label>
            <DropdownVue
              v-model="currentEntry.file_type"
              :options="fileTypes"
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder="Не задан"
            />
          </div>
          <div class="field small">
            <label>Формат файла</label>
            <DropdownVue
              v-model="currentEntry.file_format"
              :options="fileFormats"
              optionLabel="label"
              optionValue="value"
              showClear
              placeholder="Не задан"
            />
          </div>
          <div class="field small">
            <label>Тема</label>
            <InputText v-model="currentEntry.topic" />
          </div>
          <div class="field small">
            <label>Подтема</label>
            <InputText v-model="currentEntry.subtopic" />
          </div>
          <div class="field full">
            <label>Теги</label>
            <TextareaVue
              v-model="currentEntry.tags"
              rows="2"
              placeholder="Через запятую или точку с запятой"
            />
          </div>
          <div class="field full">
            <label>Описание</label>
            <TextareaVue v-model="currentEntry.description" rows="3" />
          </div>
          <div class="field full">
            <div class="transcriptions-head">
              <label>Контент</label>
              <ButtonVue
                type="button"
                label="Добавить контент"
                icon="pi pi-plus"
                text
                size="small"
                @click="addTranscription"
              />
            </div>
            <div
              v-for="(tr, idx) in currentEntry.transcriptions"
              :key="`${idx}-${tr.id || 'new'}`"
              class="transcription-item"
            >
              <div class="transcription-title-row">
                <InputText v-model="tr.title" placeholder="Название контента" />
                <ButtonVue
                  type="button"
                  icon="pi pi-trash"
                  text
                  rounded
                  severity="danger"
                  @click="removeTranscription(idx)"
                />
              </div>
              <TextareaVue
                v-model="tr.content"
                rows="5"
                placeholder="Текст контента"
              />
            </div>
          </div>
          <div class="field full">
            <div class="transcriptions-head">
              <label>Эфир к материалу</label>
              <ButtonVue
                type="button"
                label="Добавить эфир"
                icon="pi pi-plus"
                text
                size="small"
                @click="addMaterialAir"
              />
            </div>
            <div
              v-for="(air, idx) in currentEntry.material_airs"
              :key="`${idx}-${air.title || 'new'}`"
              class="transcription-item"
            >
              <div class="transcription-title-row">
                <InputText v-model="air.title" placeholder="Название эфира" />
                <ButtonVue
                  type="button"
                  icon="pi pi-trash"
                  text
                  rounded
                  severity="danger"
                  @click="removeMaterialAir(idx)"
                />
              </div>
              <InputText
                v-model="air.url"
                placeholder="Ссылка на эфир (https://...)"
              />
            </div>
          </div>
          <div class="field favorite-toggle full">
            <ToggleSwitch v-model="currentEntry.is_favorite" />
            <span>Избранное</span>
          </div>
          <div class="field favorite-toggle full">
            <ToggleSwitch v-model="currentEntry.is_hidden" />
            <span>Скрыть материал на фронте</span>
          </div>
        </div>

        <div class="dialog-actions">
          <ButtonVue
            v-if="editMode === 'edit'"
            type="button"
            class="dialog-delete"
            label="Удалить"
            icon="pi pi-trash"
            severity="danger"
            text
            @click="confirmDelete(currentEntry)"
          />
          <ButtonVue
            type="button"
            label="Отмена"
            severity="secondary"
            text
            @click="editDialogVisible = false"
          />
          <ButtonVue type="button" label="Сохранить" @click="saveEntry" />
        </div>
      </div>
    </DialogVue>
  </div>
</template>

<style scoped>
/* === БАЗОВЫЙ ЛАЙАУТ === */
.wisdom-page {
  max-width: 1760px;
  width: 100%;
  margin: 0 auto;
  padding: 1.5rem 1rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  color: var(--p-text-color, #111827);
}

.wisdom-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.wisdom-header h1 {
  margin: 0 0 0.25rem;
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--p-text-color, #111827);
}

.subtitle {
  margin: 0;
  color: var(--p-text-muted-color, #4b5563);
  font-size: 0.95rem;
}

/* === ФИЛЬТРЫ === */
.wisdom-filters {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  background: var(--p-surface-card, #ffffff);
  border: 1px solid var(--p-surface-border, #d1d5db);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.filters-main {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.filters-secondary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.field label {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--p-text-muted-color, #4b5563);
}

.field.grow {
  flex: 1 1 240px;
}

.field.small {
  flex: 0 0 180px;
}

.favorite-toggle {
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.favorite-toggle span {
  color: var(--p-text-color, #111827);
}

.field.actions {
  margin-left: auto;
}

/* === КАРТОЧКА ТАБЛИЦЫ === */
.wisdom-table-card {
  padding: 1rem 1.1rem 1.1rem;
  border-radius: 12px;
  background: var(--p-surface-card, #ffffff);
  border: 1px solid var(--p-surface-border, #d1d5db);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.25rem;
}

.table-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.wisdom-table :deep(.p-datatable-table-container) {
  overflow-x: hidden;
}

.wisdom-table :deep(table) {
  width: 100%;
}

.wisdom-table :deep(.p-datatable-thead > tr > th),
.wisdom-table :deep(.p-datatable-tbody > tr > td) {
  white-space: normal;
  overflow-wrap: anywhere;
}

.wisdom-table :deep(.p-datatable-tbody > tr > td) {
  vertical-align: top;
  border-top: 1px solid var(--p-surface-border, #e5e7eb);
}

.title-cell {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.title-cell a {
  color: var(--p-primary-color, #2563eb);
  text-decoration: none;
}

.title-cell a:hover {
  text-decoration: underline;
}

.cover-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
  background: #f3f4f6;
}

.cover-empty {
  color: var(--p-text-muted-color, #9ca3af);
  font-size: 0.85rem;
}

.cover-editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}

.cover-preview {
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 8px;
  background: #f3f4f6;
}

.cover-editor-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.topic-sub {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.75rem;
}

.keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-width: 10rem;
}

.keyword-chip {
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: var(--p-surface-100, #f3f4f6);
  color: var(--p-text-muted-color, #4b5563);
  font-size: 0.75rem;
}

.row-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  width: 100%;
}

.dialog-delete {
  margin-right: auto;
}

.empty-state {
  padding: 1.5rem 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
}

.field.full {
  grid-column: 1 / -1;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.transcriptions-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.4rem;
}

.transcription-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.6rem;
  margin-bottom: 0.5rem;
  border: 1px solid var(--p-surface-border, #d1d5db);
  border-radius: 10px;
}

.transcription-title-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.transcription-title-row :deep(input) {
  width: 100%;
}

/* === АДАПТАЦИЯ ПОД ТЁМНУЮ ТЕМУ === */

/* PrimeVue / кастомная тёмная тема через классы */

@media (max-width: 768px) {
  .wisdom-page {
    padding-inline: 0.75rem;
  }
  .filters-main,
  .filters-secondary {
    flex-direction: column;
  }
  .field.grow,
  .field.small {
    flex: 1 1 auto;
  }
  .field.actions {
    margin-left: 0;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>

